import { useMutation } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import debounce from "debounce-promise";
import { produce } from "immer";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import * as yup from "yup";

import BaseErrors from "@components/fields/BaseErrors";
import BooleanRadioButtonGroup from "@components/fields/BooleanRadioButtonGroup";
import CareProfileInput from "@components/fields/CareProfileInput";
import DataSelect from "@components/fields/DataSelect";
import Select from "@components/fields/Select";
import styles from "@components/forms/careProfile/CareProfile.module.css";
import CareProfileFormCondition from "@components/forms/careProfile/CareProfileFormCondition";
import Form from "@components/forms/Form";
import {
  CareProfileEmploymentStatusEnum,
  CareProfileFieldNameEnum,
  CareProfileFieldScopeEnum,
  CareProfileFinancialManagerEnum,
  CareProfileHealthInsuranceTypeEnum,
  CareProfileIncomeSourceEnum,
  CareProfileInsuranceTypeEnum,
  CareProfileProviderFinancialConcernEnum,
  CareProfileUpdateDocument,
  CareProfileUpdateMutation,
  CareProfileUpdateMutationVariables,
  GetCareProfileFieldRulesQuery,
  GetJourneyWithCareProfileQuery,
  HouseholdIncomeLevelEnum
} from "@typing/Generated";
import { handleMutationResponse } from "@utils/formUtils";
import {
  optionsForBoolean,
  optionsForEmploymentStatus,
  optionsForFinancialManager,
  optionsForHealthInsuranceType,
  optionsForHouseholdIncomeLevel,
  optionsForIncomeSource,
  optionsForInsuranceType,
  optionsForIsVeteran,
  optionsForProviderFinancialConcerns
} from "@utils/optionUtils";

type CareProfileFieldRules = GetCareProfileFieldRulesQuery["careProfileFieldRules"];

type Props = {
  careProfileFieldRules: CareProfileFieldRules;
  journey: GetJourneyWithCareProfileQuery["journey"];
};

type FormData = {
  employmentStatus?: CareProfileEmploymentStatusEnum | null;
  financialManager?: CareProfileFinancialManagerEnum | null;
  financialManagerOther?: string | null;
  governmentBenefitsDetails?: string | null;
  guardianEmploymentStatus?: CareProfileEmploymentStatusEnum[];
  healthInsuranceDetails?: string | null;
  healthInsuranceTypes?: CareProfileHealthInsuranceTypeEnum[];
  healthInsuranceTypesOther?: string | null;
  householdIncome?: string | null;
  householdIncomeCurrencyCode?: string | null;
  householdIncomeLevel?: HouseholdIncomeLevelEnum | null;
  incomeSources?: CareProfileIncomeSourceEnum[];
  insuranceDetails?: string | null;
  insuranceTypes?: CareProfileInsuranceTypeEnum[];
  insuranceTypesOther?: string | null;
  isVeteran?: boolean | null;
  isVeteranGuardian?: boolean | null;
  isVeteranPartner?: boolean | null;
  providerFinancialConcernDetails?: string | null;
  providerFinancialConcerns?: CareProfileProviderFinancialConcernEnum | null;
  vaConnected?: boolean | null;
};

const CareProfileFinancialForm = ({ careProfileFieldRules, journey }: Props) => {
  const intl = useIntl();

  const careProfile = journey.careProfile;

  const defaultValues: FormData = {
    employmentStatus: careProfile.employmentStatus ?? null,
    financialManager: careProfile?.financialManager?.financialManagerOption ?? null,
    financialManagerOther: careProfile?.financialManager?.other ?? null,
    governmentBenefitsDetails: careProfile.governmentBenefitsDetails ?? null,
    guardianEmploymentStatus: careProfile.guardianEmploymentStatus,
    healthInsuranceDetails: careProfile.healthInsuranceDetails ?? null,
    healthInsuranceTypes: careProfile.healthInsuranceTypes?.healthInsuranceTypesOptions ?? [],
    healthInsuranceTypesOther: careProfile.healthInsuranceTypes?.other ?? null,
    householdIncome: careProfile.householdIncome ?? null,
    householdIncomeCurrencyCode: careProfile.householdIncomeCurrencyCode ?? null,
    householdIncomeLevel: careProfile.householdIncomeLevel ?? null,
    incomeSources: careProfile.incomeSources ?? [],
    insuranceDetails: careProfile.insuranceDetails ?? null,
    insuranceTypes: careProfile?.insuranceTypes?.insuranceTypesOptions ?? [],
    insuranceTypesOther: careProfile.insuranceTypes?.other ?? null,
    isVeteran: careProfile.isVeteran ?? null,
    isVeteranGuardian: careProfile.isVeteranGuardian ?? null,
    isVeteranPartner: careProfile.isVeteranPartner ?? null,
    providerFinancialConcernDetails: careProfile.providerFinancialConcernDetails ?? null,
    providerFinancialConcerns: careProfile.providerFinancialConcerns ?? null,
    vaConnected: careProfile.vaConnected ?? null
  };

  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const [updateCareProfile] = useMutation<CareProfileUpdateMutation, CareProfileUpdateMutationVariables>(
    CareProfileUpdateDocument
  );

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      employmentStatus: yup.string().nullable().oneOf(Object.values(CareProfileEmploymentStatusEnum)),
      financialManager: yup.string().nullable().oneOf(Object.values(CareProfileFinancialManagerEnum)),
      financialManagerOther: yup.string().nullable(),
      governmentBenefitsDetails: yup.string().nullable(),
      guardianEmploymentStatus: yup
        .array()
        .of(yup.string().oneOf(Object.values(CareProfileEmploymentStatusEnum)).defined()),
      healthInsuranceDetails: yup.string().nullable(),
      healthInsuranceTypes: yup
        .array()
        .of(yup.string().oneOf(Object.values(CareProfileHealthInsuranceTypeEnum)).defined()),
      healthInsuranceTypesOther: yup.string().nullable(),
      householdIncome: yup.string().nullable(),
      householdIncomeCurrencyCode: yup.string().nullable(),
      householdIncomeLevel: yup.string().nullable().oneOf(Object.values(HouseholdIncomeLevelEnum)),
      incomeSources: yup.array().of(yup.string().oneOf(Object.values(CareProfileIncomeSourceEnum)).defined()),
      insuranceDetails: yup.string().nullable(),
      insuranceTypes: yup.array().of(yup.string().oneOf(Object.values(CareProfileInsuranceTypeEnum)).defined()),
      insuranceTypesOther: yup.string().nullable(),
      isVeteran: yup.boolean().nullable(),
      isVeteranGuardian: yup.boolean().nullable(),
      isVeteranPartner: yup.boolean().nullable(),
      providerFinancialConcernDetails: yup.string().nullable(),
      providerFinancialConcerns: yup.string().nullable().oneOf(Object.values(CareProfileProviderFinancialConcernEnum)),
      vaConnected: yup.boolean().nullable()
    })
    .required();

  const form = useForm<FormData>({
    defaultValues,
    mode: "onChange",
    resolver: yupResolver(schema)
  });

  const submitCareProfile = useCallback(async () => {
    const valuesWithoutErrors = produce(form.getValues(), draftValues => {
      Object.keys(form.formState.errors).forEach(field => {
        delete draftValues[field as keyof FormData];
      });
    });

    setBaseErrors([]);

    const financialManager = valuesWithoutErrors.financialManager;
    const healthInsuranceTypes = valuesWithoutErrors.healthInsuranceTypes;
    const insuranceTypes = valuesWithoutErrors.insuranceTypes;

    return updateCareProfile({
      variables: {
        ...valuesWithoutErrors,
        financialManager: financialManager && {
          financialManagerOption: financialManager,
          other:
            financialManager === CareProfileFinancialManagerEnum.OTHER
              ? valuesWithoutErrors.financialManagerOther
              : null
        },
        healthInsuranceTypes: healthInsuranceTypes && {
          healthInsuranceTypesOptions: healthInsuranceTypes,
          other: healthInsuranceTypes.includes(CareProfileHealthInsuranceTypeEnum.OTHER)
            ? valuesWithoutErrors.healthInsuranceTypesOther
            : null
        },
        id: journey.careProfile.id,
        insuranceTypes: insuranceTypes && {
          insuranceTypesOptions: insuranceTypes,
          other: insuranceTypes.includes(CareProfileInsuranceTypeEnum.OTHER)
            ? valuesWithoutErrors.insuranceTypesOther
            : null
        }
      }
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response.data?.careProfileUpdate,
        intl,
        onError: () => {},
        onSuccess: () => {},
        setBaseErrors,
        setError: form.setError,
        txBase: "errors.careProfile"
      });
    });
  }, [form, intl, journey.careProfile.id, updateCareProfile]);

  useEffect(() => {
    const debouncedSubmit = debounce(submitCareProfile, 2000);
    const subscription = form.watch(() => form.handleSubmit(debouncedSubmit, debouncedSubmit)());
    return () => subscription.unsubscribe();
  }, [form, submitCareProfile]);

  const booleanOptions = optionsForBoolean(intl);
  const employmentStatusOptions = optionsForEmploymentStatus(intl);
  const financialManagerOptions = optionsForFinancialManager(intl);
  const healthInsuranceTypeOptions = optionsForHealthInsuranceType(intl);
  const householdIncomeLevelOptions = optionsForHouseholdIncomeLevel(intl);
  const incomeSourceOptions = optionsForIncomeSource(intl);
  const insuranceTypeOptions = optionsForInsuranceType(intl);
  const isVeteranOptions = optionsForIsVeteran(intl);
  const providerFinancialConcernsOptions = optionsForProviderFinancialConcerns(intl);

  const incomeSources = form.watch("incomeSources");
  const isVeteranOwner = journey.owner.isVeteran;
  const isVeteran = form.watch("isVeteran");
  const isVeteranGuardian = form.watch("isVeteranGuardian");
  const isVeteranPartner = form.watch("isVeteranPartner");
  const providerFinancialConcerns = form.watch("providerFinancialConcerns");

  return (
    <Form hookForm={form}>
      <BaseErrors errors={baseErrors} />
      <div className={styles.formSectionRow}>
        <div className={styles.formControlsColumn}>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.EMPLOYMENT_STATUS}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="employmentStatus"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <Select<FormData>
              clearable
              control={form.control}
              error={form.formState.errors.employmentStatus}
              label={intl.formatMessage({ id: "forms.careProfileNew.employmentStatusLabel" })}
              name="employmentStatus"
              options={employmentStatusOptions}
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.GUARDIAN_EMPLOYMENT_STATUS}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="guardianEmploymentStatus"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <Select<FormData>
              control={form.control}
              error={form.formState.errors.guardianEmploymentStatus}
              isMulti
              label={intl.formatMessage({ id: "forms.careProfileNew.guardianEmploymentStatusLabel" })}
              name="guardianEmploymentStatus"
              options={employmentStatusOptions}
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.HEALTH_INSURANCE_TYPES}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="healthInsuranceTypes"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <Select<FormData>
              control={form.control}
              error={form.formState.errors.healthInsuranceTypes}
              isMulti
              label={intl.formatMessage({ id: "forms.careProfileNew.healthInsuranceTypesLabel" })}
              mutuallyExclusiveValues={[CareProfileHealthInsuranceTypeEnum.NONE]}
              name="healthInsuranceTypes"
              options={healthInsuranceTypeOptions}
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.HEALTH_INSURANCE_TYPES_OTHER}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="healthInsuranceTypesOther"
            otherField="healthInsuranceTypes"
            otherValue={CareProfileHealthInsuranceTypeEnum.OTHER}
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <CareProfileInput<FormData>
              error={form.formState.errors.healthInsuranceTypesOther}
              label={intl.formatMessage({ id: "forms.careProfileNew.healthInsuranceTypesOtherLabel" })}
              register={form.register("healthInsuranceTypesOther")}
              spellcheck
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.HEALTH_INSURANCE_DETAILS}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="healthInsuranceDetails"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <CareProfileInput<FormData>
              error={form.formState.errors.healthInsuranceDetails}
              label={intl.formatMessage({ id: "forms.careProfileNew.healthInsuranceDetailsLabel" })}
              register={form.register("healthInsuranceDetails")}
              spellcheck
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.INSURANCE_TYPES}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="insuranceTypes"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <Select<FormData>
              control={form.control}
              error={form.formState.errors.insuranceTypes}
              isMulti
              label={intl.formatMessage({ id: "forms.careProfileNew.insuranceTypesLabel" })}
              mutuallyExclusiveValues={[CareProfileInsuranceTypeEnum.NONE]}
              name="insuranceTypes"
              options={insuranceTypeOptions}
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.INSURANCE_TYPES_OTHER}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="insuranceTypesOther"
            otherField="insuranceTypes"
            otherValue={CareProfileInsuranceTypeEnum.OTHER}
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <CareProfileInput<FormData>
              error={form.formState.errors.insuranceTypesOther}
              label={intl.formatMessage({ id: "forms.careProfileNew.insuranceTypesOtherLabel" })}
              register={form.register("insuranceTypesOther")}
              spellcheck
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.INSURANCE_DETAILS}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="insuranceDetails"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <CareProfileInput<FormData>
              error={form.formState.errors.insuranceDetails}
              label={intl.formatMessage({ id: "forms.careProfileNew.insuranceDetailsLabel" })}
              register={form.register("insuranceDetails")}
              spellcheck
            />
          </CareProfileFormCondition>
        </div>
        <div className={styles.formControlsColumn}>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.PROVIDER_FINANCIAL_CONCERNS}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="providerFinancialConcerns"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <Select<FormData>
              clearable
              control={form.control}
              error={form.formState.errors.providerFinancialConcerns}
              label={intl.formatMessage({ id: "forms.careProfileNew.providerFinancialConcernsLabel" })}
              name="providerFinancialConcerns"
              options={providerFinancialConcernsOptions}
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            condition={providerFinancialConcerns === CareProfileProviderFinancialConcernEnum.YES}
            fieldName={CareProfileFieldNameEnum.PROVIDER_FINANCIAL_CONCERN_DETAILS}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="providerFinancialConcernDetails"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <CareProfileInput<FormData>
              error={form.formState.errors.providerFinancialConcernDetails}
              label={intl.formatMessage({ id: "forms.careProfileNew.providerFinancialConcernDetailsLabel" })}
              register={form.register("providerFinancialConcernDetails")}
              spellcheck
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.INCOME_SOURCES}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="incomeSources"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <Select<FormData>
              control={form.control}
              error={form.formState.errors.incomeSources}
              isMulti
              label={intl.formatMessage({ id: "forms.careProfileNew.incomeSourcesLabel" })}
              name="incomeSources"
              options={incomeSourceOptions}
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            condition={incomeSources?.includes(CareProfileIncomeSourceEnum.GOVERNMENT_PROGRAMS_OR_BENEFITS)}
            fieldName={CareProfileFieldNameEnum.GOVERNMENT_BENEFITS_DETAILS}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="governmentBenefitsDetails"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <CareProfileInput<FormData>
              error={form.formState.errors.governmentBenefitsDetails}
              label={intl.formatMessage({ id: "forms.careProfileNew.governmentBenefitsDetailsLabel" })}
              register={form.register("governmentBenefitsDetails")}
              spellcheck
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.IS_VETERAN}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="isVeteran"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <BooleanRadioButtonGroup
              allowDeselection
              control={form.control}
              error={form.formState.errors.isVeteran}
              label={intl.formatMessage({ id: "forms.careProfileNew.isVeteranLabel" })}
              name="isVeteran"
              options={isVeteranOptions}
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.IS_VETERAN_PARTNER}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="isVeteranPartner"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <BooleanRadioButtonGroup
              allowDeselection
              control={form.control}
              error={form.formState.errors.isVeteranPartner}
              label={intl.formatMessage({ id: "forms.careProfileNew.isVeteranPartnerLabel" })}
              name="isVeteranPartner"
              options={isVeteranOptions}
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.IS_VETERAN_GUARDIAN}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="isVeteranGuardian"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <BooleanRadioButtonGroup
              allowDeselection
              control={form.control}
              error={form.formState.errors.isVeteranGuardian}
              label={intl.formatMessage({ id: "forms.careProfileNew.isVeteranGuardianLabel" })}
              name="isVeteranGuardian"
              options={isVeteranOptions}
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            condition={!!(isVeteran || isVeteranGuardian || isVeteranPartner || isVeteranOwner)}
            fieldName={CareProfileFieldNameEnum.VA_CONNECTED}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="vaConnected"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <BooleanRadioButtonGroup
              allowDeselection
              control={form.control}
              error={form.formState.errors.vaConnected}
              label={intl.formatMessage({ id: "forms.careProfileNew.vaConnectedLabel" })}
              name="vaConnected"
              options={booleanOptions}
            />
          </CareProfileFormCondition>
        </div>
        <div className={styles.formControlsColumn}>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.HOUSEHOLD_INCOME_LEVEL}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="householdIncomeLevel"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <Select<FormData>
              clearable
              control={form.control}
              error={form.formState.errors.householdIncomeLevel}
              label={intl.formatMessage({ id: "forms.careProfileNew.householdIncomeLevelLabel" })}
              name="householdIncomeLevel"
              options={householdIncomeLevelOptions}
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.HOUSEHOLD_INCOME_CURRENCY_CODE}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="householdIncomeCurrencyCode"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <DataSelect<FormData>
              control={form.control}
              error={form.formState.errors.householdIncomeCurrencyCode}
              label={intl.formatMessage({ id: "forms.careProfileNew.householdIncomeCurrencyCodeLabel" })}
              name="householdIncomeCurrencyCode"
              type="currencies"
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.HOUSEHOLD_INCOME}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="householdIncome"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <CareProfileInput<FormData>
              error={form.formState.errors.householdIncome}
              label={intl.formatMessage({ id: "forms.careProfileNew.householdIncomeLabel" })}
              register={form.register("householdIncome")}
              spellcheck
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.FINANCIAL_MANAGER}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="financialManager"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <Select<FormData>
              clearable
              control={form.control}
              error={form.formState.errors.financialManager}
              label={intl.formatMessage({ id: "forms.careProfileNew.financialManagerLabel" })}
              name="financialManager"
              options={financialManagerOptions}
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.FINANCIAL_MANAGER_OTHER}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="financialManagerOther"
            otherField="financialManager"
            otherValue={CareProfileFinancialManagerEnum.OTHER}
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <CareProfileInput<FormData>
              error={form.formState.errors.financialManagerOther}
              label={intl.formatMessage({ id: "forms.careProfileNew.financialManagerOtherLabel" })}
              register={form.register("financialManagerOther")}
              spellcheck
            />
          </CareProfileFormCondition>
        </div>
      </div>
    </Form>
  );
};

export default CareProfileFinancialForm;
